import { flatten } from 'lodash'
import * as Sentry from '@sentry/vue'
import PresentationApi from '@/api/presentation'
import util from '@/util'
import store from '@/vuex/store'
import CommunityApi from '@/api/community'
import CategoryApi from '@/api/category'
import Config from '@/config'
import SlideApi from '@/api/slide'
import EventBus from '@/eventBus'

const BY_COMMUNITY = 'By community'

const GET_TEMPLATES_ADMIN_PICKED_START = 'GET_TEMPLATES_ADMIN_PICKED_START'
const GET_TEMPLATES_ADMIN_PICKED_SUCCEEDED = 'GET_TEMPLATES_ADMIN_PICKED_SUCCEEDED'
const GET_TEMPLATES_ADMIN_PICKED_FAILED = 'GET_TEMPLATES_ADMIN_PICKED_FAILED'

const GET_TEMPLATES_START = 'GET_TEMPLATES_START'
const GET_TEMPLATES_SUCCEEDED = 'GET_TEMPLATES_SUCCEEDED'
const GET_TEMPLATES_PANEL_SUCCEEDED = 'GET_TEMPLATES_PANEL_SUCCEEDED'
const GET_TEMPLATES_FAILED = 'GET_TEMPLATES_FAILED'

const GET_NEWLY_ADDED_START = 'GET_NEWLY_ADDED_START'
const GET_NEWLY_ADDED_SUCCEEDED = 'GET_NEWLY_ADDED_SUCCEEDED'
const GET_NEWLY_ADDED_FAILED = 'GET_NEWLY_ADDED_FAILED'
const UPDATE_NEWLY_ADDED_AFTER_PUBLISH = 'UPDATE_NEWLY_ADDED_AFTER_PUBLISH'

const COPY_TEMPLATE_START = 'COPY_TEMPLATE_START'
const COPY_TEMPLATE_SUCCEEDED = 'COPY_TEMPLATE_SUCCEEDED'
const COPY_TEMPLATE_FAILED = 'COPY_TEMPLATE_FAILED'

const GET_TEMPLATES_COMMUNITY_V2 = 'GET_TEMPLATES_COMMUNITY_V2'

const NUMBER_OF_NEW_TEMPLATES_ITEMS = 20

const _state = {
  templateCategories: null,
  templatePanel: null,
  templateAdminPicked: null,
  templateAdminPickedForMobileFull: null,
  templateAdminPickedForMobileMini: null,
  isLoadingTemplates: true,
  isLoadingTemplatesPanel: true,
  getTemplatesError: null,
  newlyAddedCommunityTemplates: null,
  newlyAddedCommunityTemplatesForMobileMini: null,
  newlyAddedCommunityTemplatesForMobileFull: null,
  isLoadingNewlyAdded: true,
  isLoadingTemplateAdminPicked: true,
  getNewlyAddedError: null,
  getTemplateAdminPickedError: null,
  copyingTemplateId: null,
  copyError: false,
  templateCommunityV2: null,
}

const sortByUpdatedDate = (a, b) => {
  return new Date(b.updatedAt) - new Date(a.updatedAt)
}

const sortByDownload = (a, b) => {
  return b.copyCount - a.copyCount
}

const sortByTag = (a, b, tag) => {
  return b.tags.includes(tag) - a.tags.includes(tag)
}

const sortByPin = (a, b) => {
  return b.tags.includes('pin') - a.tags.includes('pin')
}

const removeLowQualityPresentation = (list) => {
  const blackList = [
    'My new presentation',
    'Your saved Spinner Wheel',
    'Your saved WordCloud',
  ]
  const whiteList = [
    'General Knowledge Quiz',
  ]
  return list.filter((item) => {
    if (whiteList.includes(item.name)) {
      return true
    }
    if (parseInt(item.slideCount, 10) < 3) {
      return false
    }
    if (blackList.includes(item.name)) {
      return false
    }
    if (item.User.type === 'official') {
      return false
    }
    return true
  })
}

const getters = {
  templateCommunityV2(state) {
    return state.templateCommunityV2
  },
  templateCategories(state) {
    return state.templateCategories
  },
  templatePanel(state) {
    return state.templatePanel
  },
  templateAdminPicked(state) {
    return state.templateAdminPicked
  },
  templateAdminPickedForMobileFull(state) {
    return state.templateAdminPickedForMobileFull
  },
  templateAdminPickedForMobileMini(state) {
    return state.templateAdminPickedForMobileMini
  },
  isLoadingTemplates(state) {
    return state.isLoadingTemplates
  },
  isLoadingTemplatePanel(state) {
    return state.isLoadingTemplatesPanel
  },
  templatesError(state) {
    return state.getTemplatesError
  },
  newlyAddedCommunityTemplates(state) {
    return state.newlyAddedCommunityTemplates
  },
  newlyAddedCommunityTemplatesForMobileMini(state) {
    return state.newlyAddedCommunityTemplatesForMobileMini
  },
  newlyAddedCommunityTemplatesForMobileFull(state) {
    return state.newlyAddedCommunityTemplatesForMobileFull
  },
  isLoadingNewlyAdded(state) {
    return state.isLoadingNewlyAdded
  },
  isLoadingTemplateAdminPicked(state) {
    return state.isLoadingTemplateAdminPicked
  },
  getNewlyAddedError(state) {
    return state.getgetNewlyAddedError
  },
  getTemplateAdminPickedError(state) {
    return state.getTemplateAdminPickedError
  },
  copyingTemplateId(state) {
    return state.copyingTemplateId
  },
  trendingTemplates(state) {
    let templates = flatten(state.templateCategories.map((category) => {
      if (!category.presentations) return []
      return category.presentations
        .sort(sortByUpdatedDate)
        .slice(0, 3)
    }))

    templates.sort(sortByUpdatedDate)

    templates = templates.map((item) => {
      if (!item.tags) item.tags = []
      return item
    })
    if (store.state.currentUser) {
      const { personalInformation } = store.state.currentUser
      if (personalInformation && personalInformation.useAhaSlidesFor) {
        templates = templates.sort((a, b) => {
          return sortByTag(a, b, personalInformation.useAhaSlidesFor)
        })
      }
    }
    return templates
  },
  topTemplates(state) {
    let templates = flatten(state.templatePanel.map((category) => {
      if (!category.presentations) return []
      return category.presentations
    }))
    templates = templates.sort(sortByUpdatedDate)
    templates = templates.map((item) => {
      if (!item.tags) item.tags = []
      return item
    })
    templates = templates.filter((item) => (item.User.type === 'official'))
    if (store.state.currentUser) {
      const { personalInformation } = store.state.currentUser
      if (personalInformation && personalInformation.useAhaSlidesFor) {
        templates = templates.sort((a, b) => {
          return sortByTag(a, b, personalInformation.useAhaSlidesFor)
        })
      }
    }
    templates = templates.sort(sortByPin)
    return templates
  },

}

const actions = {
  getTemplatesAdminPickedAction: async ({ commit }) => {
    commit(GET_TEMPLATES_ADMIN_PICKED_START)
    try {
      const response = await PresentationApi.getTemplatesAdminPicked()

      const shuffledTemplates = response.templates.sort(() => 0.5 - Math.random())

      commit(GET_TEMPLATES_ADMIN_PICKED_SUCCEEDED, shuffledTemplates)
      return true
    } catch (error) {
      Sentry.captureException(error)
      commit(GET_TEMPLATES_ADMIN_PICKED_FAILED, error)
      return false
    }
  },
  getTemplatesOldAction: async ({ commit }) => {
    commit(GET_TEMPLATES_START)
    try {
      const response = await PresentationApi.getTemplates()
      response.folders = response.folders.map((f) => {
        f.presentations = f.presentations.sort(sortByDownload)
        return f
      })
      commit(GET_TEMPLATES_SUCCEEDED, response.folders)
      return true
    } catch (error) {
      Sentry.captureException(error)
      commit(GET_TEMPLATES_FAILED, error)
      return false
    }
  },
  /**
   * @deprecated this function is deprecated coz it's not correct, it should be removed
   * Note: use getCategoryTemplates instead
   */
  getTemplatesAction: async ({ commit }) => {
    commit(GET_TEMPLATES_START)
    try {
      const presentationNewlyAdded = await CommunityApi.getNewlyAddedPublicPresentations()
      const qualityPresentationsNewlyAdded = removeLowQualityPresentation(presentationNewlyAdded)
      const response = await PresentationApi.getTemplates()
      response.folders.forEach((item) => {
        if (item.presentations) {
          item.presentations = item.presentations.filter((p) => p.isPublicSearch)
        }
      })
      response.folders = response.folders.filter((item) => item.presentations && item.presentations.length)

      // Because template categories and template panel using same response,
      // we must deep copy new version
      const panelTemplates = JSON.parse(JSON.stringify(response.folders))
      commit(GET_TEMPLATES_PANEL_SUCCEEDED, panelTemplates)
      const folderNewlyAdded = {
        folderName: BY_COMMUNITY,
        folderId: 999999999,
        presentations: qualityPresentationsNewlyAdded,
      }
      response.folders.push(folderNewlyAdded)
      response.folders = response.folders.map((f) => {
        f.presentations = f.presentations.sort(sortByDownload).slice(0, 12)
        return f
      })
      commit(GET_TEMPLATES_SUCCEEDED, response.folders)

      // hardcode community category id (env prod: 19 for global, 1 for eu)
      const communityCategoryId = Config.region === 'gb' ? 19 : 1

      commit(GET_TEMPLATES_COMMUNITY_V2, [{
        categoryId: communityCategoryId,
        categoryName: BY_COMMUNITY,
        presentations: qualityPresentationsNewlyAdded,
      }])

      return true
    } catch (error) {
      Sentry.captureException(error)
      commit(GET_TEMPLATES_FAILED, error)
      return false
    }
  },
  getCategoryTemplates: async ({ commit }) => {
    commit(GET_TEMPLATES_START)
    try {
      // hardcode community category id (env prod: 19 for global, 1 for eu)
      // updated community category id for develop: 22 (should update here insteadof hardcode)
      let communityCategoryId
      if (Config.region === 'gb') {
        communityCategoryId = Config.env === 'develop' ? 22 : 19
      } else {
        communityCategoryId = 1
      }

      const queryParams = {
        id: communityCategoryId,
        isAdminPick: true,
      }
      const presentationNewlyAdded = await CategoryApi.getCategoryTemplates(queryParams)
      const qualityPresentationsNewlyAdded =
        presentationNewlyAdded.PresentationsCategories.map((item) => item.Presentation)
      const response = await PresentationApi.getTemplates()

      // Because template categories and template panel using same response,
      // we must deep copy new version
      const panelTemplates = JSON.parse(JSON.stringify(response.folders))
      commit(GET_TEMPLATES_PANEL_SUCCEEDED, panelTemplates)
      const folderNewlyAdded = {
        folderName: BY_COMMUNITY,
        folderId: 999999999,
        presentations: qualityPresentationsNewlyAdded,
      }
      response.folders.push(folderNewlyAdded)
      commit(GET_TEMPLATES_SUCCEEDED, response.folders)

      commit(GET_TEMPLATES_COMMUNITY_V2, [{
        categoryId: communityCategoryId,
        categoryName: BY_COMMUNITY,
        presentations: qualityPresentationsNewlyAdded,
      }])

      return true
    } catch (error) {
      Sentry.captureException(error)
      commit(GET_TEMPLATES_FAILED, error)
      return false
    }
  },
  getNewlyAddedOldAction: async ({ commit }) => {
    commit(GET_NEWLY_ADDED_START)
    try {
      const presentations = await CommunityApi.getNewlyAddedPublicPresentations()
      const qualityPresentations = removeLowQualityPresentation(presentations)
      commit(GET_NEWLY_ADDED_SUCCEEDED, qualityPresentations)
      return true
    } catch (error) {
      Sentry.captureException(error)
      commit(GET_NEWLY_ADDED_FAILED, error)
      return false
    }
  },
  getNewlyAddedAction: async ({ commit }) => {
    commit(GET_NEWLY_ADDED_START)
    try {
      const templates = await PresentationApi.getNewTemplates({ limit: NUMBER_OF_NEW_TEMPLATES_ITEMS })

      commit(GET_NEWLY_ADDED_SUCCEEDED, templates)
      return true
    } catch (error) {
      Sentry.captureException(error)
      commit(GET_NEWLY_ADDED_FAILED, error)
      return false
    }
  },
  updateNewlyAddedAfterPublishAction: ({ commit }, payload) => {
    commit(UPDATE_NEWLY_ADDED_AFTER_PUBLISH, payload)
  },
  copyFromTemplateAction: async ({ commit, state }, { template, callback, isTracking = true }) => {
    if (state.copyingTemplateId) return
    commit(COPY_TEMPLATE_START, template.id)
    try {
      const copiedPresentation = await PresentationApi.copyTemplateToAccount(template)

      if (isTracking) {
        util.mixpanelTracking('Presentation List', 'Create new presentation', 'Use template', null, {
          sourcePresentationId: template.id,
          presentationId: copiedPresentation.id,
        })
      }

      commit(COPY_TEMPLATE_SUCCEEDED)
      callback(copiedPresentation)
    } catch (error) {
      Sentry.captureException(error)
      commit(COPY_TEMPLATE_FAILED, error)
    }
  },

  // Errors intentionally bubble up — the deep-link route view owns Sentry capture + fallback nav.
  createFromTemplateDeepLinkAction: async (_, { templateId, deepLinkSource = 'unknown' }) => {
    const copiedPresentation = await PresentationApi.copyTemplateToAccount({ id: templateId })

    util.mixpanelTracking('Presentation List', 'Create new presentation', 'Use template', null, {
      sourcePresentationId: templateId,
      presentationId: copiedPresentation.id,
      deepLinkSource,
    })

    return copiedPresentation
  },

  copySlidesAction: async (context, presentationid) => {
    const resp = await SlideApi.getListSlidesByPresentationId(presentationid)
    const payload = {
      destinatedPresentationId: store.state.presentation.id,
      isDuplicatingToOtherPresentation: true,
      isIncludingResponseData: false,
      duplicatedSlideId: resp.body.map((item) => item.id),
    }
    let index = store.state.presentation.slides.findIndex((item) => item.id === store.state.slideActive.id)
    if (index === -1) {
      index = store.state.presentation.slides.length - 1
    }

    payload.insertAfter = store.state.presentation.slides?.[index]?.order
    const respDuplicate = await SlideApi.duplicateOtherPresentation(payload)
    respDuplicate.body.slides = respDuplicate.body.slides.map((item) => {
      if (item.additionalFields) {
        item.additionalFields = JSON.parse(item.additionalFields)
      }
      return item
    })

    await store.dispatch('addSlidesAction', {
      slides: respDuplicate.body.slides,
      insertAfter: index,
    })
    store.dispatch('setIsNewPresentationAction', false)
    store.dispatch('setShowModalTemplateLibraryAction', false)

    EventBus.$emit('SideLeft: deselectAllSlides')
    EventBus.$emit('makeSlideActive', store.state.presentation.slides[index + 1])
  },
}

const mutations = {
  [GET_TEMPLATES_START](state) {
    state.isLoadingTemplates = true
  },
  [GET_TEMPLATES_SUCCEEDED](state, templateCategories) {
    state.templateCategories = templateCategories
    state.isLoadingTemplates = false
  },
  [GET_TEMPLATES_PANEL_SUCCEEDED](state, templates) {
    state.templatePanel = templates
    state.isLoadingTemplatesPanel = false
  },
  [GET_TEMPLATES_FAILED](state, error) {
    state.getTemplatesError = error
    state.isLoadingTemplates = false
    state.isLoadingTemplatePanel = false
  },

  [GET_NEWLY_ADDED_START](state) {
    state.isLoadingNewlyAdded = true
  },
  [GET_TEMPLATES_ADMIN_PICKED_START](state) {
    state.isLoadingTemplateAdminPicked = true
  },
  [GET_TEMPLATES_ADMIN_PICKED_FAILED](state, error) {
    state.getTemplateAdminPickedError = error
    state.isLoadingTemplateAdminPicked = false
  },
  [GET_TEMPLATES_ADMIN_PICKED_SUCCEEDED](state, templateCategories) {
    templateCategories = templateCategories.filter((item) => item.isPublicSearch)
    state.templateAdminPicked = JSON.parse(JSON.stringify(templateCategories))
    const mobileFull = templateCategories.splice(0, 10)
    state.templateAdminPickedForMobileMini = mobileFull.splice(0, 3)
    state.templateAdminPickedForMobileFull = mobileFull
    state.isLoadingTemplateAdminPicked = false
  },
  [UPDATE_NEWLY_ADDED_AFTER_PUBLISH](state, newly) {
    const latestList = state.newlyAddedCommunityTemplates.map((item) => {
      if (item.id === newly.id) {
        if (newly.customThumbnailImage) {
          item.customThumbnailImage = newly.customThumbnailImage
        }
        item.description = newly.description
        item.name = newly.name
      }
      return item
    })
    state.newlyAddedCommunityTemplates = latestList
  },
  [GET_NEWLY_ADDED_SUCCEEDED](state, newlyAddedCommunityTemplates) {
    state.newlyAddedCommunityTemplates = JSON.parse(JSON.stringify(newlyAddedCommunityTemplates))
    const mobileFull = newlyAddedCommunityTemplates.splice(0, 10)
    state.newlyAddedCommunityTemplatesForMobileMini = mobileFull.splice(0, 3)
    state.newlyAddedCommunityTemplatesForMobileFull = mobileFull
    state.isLoadingNewlyAdded = false
  },
  [GET_NEWLY_ADDED_FAILED](state, error) {
    state.getNewlyAddedError = error
    state.isLoadingNewlyAdded = false
  },

  [COPY_TEMPLATE_START](state, templateId) {
    state.copyingTemplateId = templateId
  },

  [COPY_TEMPLATE_SUCCEEDED](state) {
    state.copyingTemplateId = null
  },

  [COPY_TEMPLATE_FAILED](state, error) {
    state.copyError = error
    state.copyingTemplateId = null
  },
  [GET_TEMPLATES_COMMUNITY_V2](state, templateCategories) {
    state.templateCommunityV2 = templateCategories
    state.isLoadingTemplates = false
  },
}

export default {
  namespaced: true,
  state: _state,
  getters,
  actions,
  mutations,
}
